import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  writeBatch,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';

// Taxonomy collection names
const TAXONOMY_COLLECTION = 'taxonomy';
const PRODUCTS_COLLECTION = 'products';

// Taxonomy categories
export const TAXONOMY_CATEGORIES = {
  listingType: {
    name: 'Listing Type',
    description: 'Product category type',
    required: true,
    singleValue: true,
    defaultValue: 'meal_prep'
  },
  itemCategory: {
    name: 'Item Category',
    description: 'Subcategory of item',
    required: true,
    singleValue: true,
    defaultValue: 'entree'
  },
  cuisine: {
    name: 'Cuisine',
    description: 'Style/region',
    required: false,
    singleValue: true,
    defaultValue: null
  },
  dietaryTags: {
    name: 'Dietary Tags',
    description: 'Dietary & lifestyle tags',
    required: false,
    singleValue: false,
    defaultValue: []
  },
  courseType: {
    name: 'Course Type',
    description: 'Meal/course classification',
    required: false,
    singleValue: true,
    defaultValue: null
  },
  dishType: {
    name: 'Dish Type',
    description: 'Dish preparation/style',
    required: false,
    singleValue: true,
    defaultValue: null
  }
};

// Default taxonomy values with display names
export const DEFAULT_TAXONOMY_VALUES = {
  listingType: [
    { value: 'bake_at_home', display: 'Bake at Home' },
    { value: 'in_person_chef', display: 'In-Person Chef' },
    { value: 'meal_prep', display: 'Meal Prep' },
    { value: 'ready_to_eat', display: 'Ready to Eat' }
  ],
  itemCategory: [
    { value: 'baked_goods', display: 'Baked Goods' },
    { value: 'entree', display: 'Entree' },
    { value: 'side', display: 'Side Dish' },
    { value: 'course', display: 'Course' },
    { value: 'preserves', display: 'Preserves' },
    { value: 'candy', display: 'Candy' }
  ],
  cuisine: [
    { value: 'american', display: 'American' },
    { value: 'italian', display: 'Italian' },
    { value: 'indian', display: 'Indian' },
    { value: 'mexican', display: 'Mexican' },
    { value: 'chinese', display: 'Chinese' },
    { value: 'japanese', display: 'Japanese' },
    { value: 'thai', display: 'Thai' },
    { value: 'vietnamese', display: 'Vietnamese' },
    { value: 'mediterranean', display: 'Mediterranean' },
    { value: 'middle_eastern', display: 'Middle Eastern' },
    { value: 'greek', display: 'Greek' },
    { value: 'french', display: 'French' },
    { value: 'spanish', display: 'Spanish' },
    { value: 'korean', display: 'Korean' },
    { value: 'caribbean', display: 'Caribbean' },
    { value: 'turkish', display: 'Turkish' },
    { value: 'persian', display: 'Persian' },
    { value: 'ethiopian', display: 'Ethiopian' },
    { value: 'filipino', display: 'Filipino' },
    { value: 'brazilian', display: 'Brazilian' }
  ],
  dietaryTags: [
    { value: 'vegan', display: 'Vegan' },
    { value: 'vegetarian', display: 'Vegetarian' },
    { value: 'pescatarian', display: 'Pescatarian' },
    { value: 'keto', display: 'Keto' },
    { value: 'paleo', display: 'Paleo' },
    { value: 'whole30', display: 'Whole30' },
    { value: 'low_carb', display: 'Low Carb' },
    { value: 'low_sugar', display: 'Low Sugar' },
    { value: 'sugar_free', display: 'Sugar Free' },
    { value: 'low_sodium', display: 'Low Sodium' },
    { value: 'high_protein', display: 'High Protein' },
    { value: 'halal_friendly', display: 'Halal Friendly' },
    { value: 'kosher_style', display: 'Kosher Style' },
    { value: 'gluten_free', display: 'Gluten Free' },
    { value: 'dairy_free', display: 'Dairy Free' },
    { value: 'egg_free', display: 'Egg Free' },
    { value: 'soy_free', display: 'Soy Free' },
    { value: 'peanut_free', display: 'Peanut Free' },
    { value: 'tree_nut_free', display: 'Tree Nut Free' },
    { value: 'sesame_free', display: 'Sesame Free' },
    { value: 'shellfish_free', display: 'Shellfish Free' },
    { value: 'pork_free', display: 'Pork Free' }
  ],
  courseType: [
    { value: 'breakfast', display: 'Breakfast' },
    { value: 'brunch', display: 'Brunch' },
    { value: 'lunch', display: 'Lunch' },
    { value: 'dinner', display: 'Dinner' },
    { value: 'appetizer', display: 'Appetizer' },
    { value: 'side', display: 'Side' },
    { value: 'main', display: 'Main Course' },
    { value: 'dessert', display: 'Dessert' },
    { value: 'snack', display: 'Snack' }
  ],
  dishType: [
    { value: 'bread', display: 'Bread' },
    { value: 'pizza_flatbread', display: 'Pizza & Flatbread' },
    { value: 'sandwich_wrap', display: 'Sandwich & Wrap' },
    { value: 'burger', display: 'Burger' },
    { value: 'taco', display: 'Taco' },
    { value: 'burrito', display: 'Burrito' },
    { value: 'pasta_noodles', display: 'Pasta & Noodles' },
    { value: 'noodle_soup', display: 'Noodle Soup' },
    { value: 'rice_grains', display: 'Rice & Grains' },
    { value: 'grain_bowl', display: 'Grain Bowl' },
    { value: 'salad', display: 'Salad' },
    { value: 'soup_stew', display: 'Soup & Stew' },
    { value: 'curry', display: 'Curry' },
    { value: 'stir_fry', display: 'Stir Fry' },
    { value: 'casserole_bake', display: 'Casserole & Bake' },
    { value: 'roast_grill_plate', display: 'Roast, Grill & Plate' },
    { value: 'skewer_kebab', display: 'Skewer & Kebab' },
    { value: 'dumpling_bun', display: 'Dumpling & Bun' },
    { value: 'sushi_onigiri', display: 'Sushi & Onigiri' },
    { value: 'poke_bowl', display: 'Poke Bowl' },
    { value: 'seafood_plate', display: 'Seafood Plate' },
    { value: 'side_vegetable', display: 'Side Vegetable' },
    { value: 'side_starch', display: 'Side Starch' },
    { value: 'dip_spread', display: 'Dip & Spread' },
    { value: 'sauce_condiment', display: 'Sauce & Condiment' },
    { value: 'pastry_sweet', display: 'Pastry & Sweet' },
    { value: 'cake', display: 'Cake' },
    { value: 'pie_tart', display: 'Pie & Tart' },
    { value: 'cookie_bar', display: 'Cookie & Bar' },
    { value: 'brownie', display: 'Brownie' },
    { value: 'donut', display: 'Donut' },
    { value: 'muffin_scone', display: 'Muffin & Scone' },
    { value: 'cheesecake', display: 'Cheesecake' },
    { value: 'confection_candy', display: 'Confection & Candy' },
    { value: 'pudding_custard', display: 'Pudding & Custard' },
    { value: 'ice_cream_frozen', display: 'Ice Cream & Frozen' },
    { value: 'beverage_hot', display: 'Hot Beverage' },
    { value: 'beverage_cold', display: 'Cold Beverage' },
    { value: 'jam_jelly_preserve', display: 'Jam, Jelly & Preserve' },
    { value: 'pickle_ferment', display: 'Pickle & Ferment' },
    { value: 'spice_mix', display: 'Spice & Mix' },
    { value: 'meal_kit', display: 'Meal Kit' },
    { value: 'bake_at_home_kit', display: 'Bake at Home Kit' },
    { value: 'sauce_kit', display: 'Sauce Kit' }
  ]
};

/**
 * Initialize taxonomy in database if it doesn't exist
 */
export const initializeTaxonomy = async () => {
  try {
    const taxonomyRef = collection(db, TAXONOMY_COLLECTION);
    const snapshot = await getDocs(taxonomyRef);
    
    if (snapshot.empty) {
      console.log('Initializing taxonomy with default values...');
      
      const batch = writeBatch(db);
      
      Object.entries(DEFAULT_TAXONOMY_VALUES).forEach(([category, values]) => {
        const categoryRef = doc(taxonomyRef, category);
        batch.set(categoryRef, {
          name: TAXONOMY_CATEGORIES[category].name,
          description: TAXONOMY_CATEGORIES[category].description,
          required: TAXONOMY_CATEGORIES[category].required,
          singleValue: TAXONOMY_CATEGORIES[category].singleValue,
          defaultValue: TAXONOMY_CATEGORIES[category].defaultValue,
          values: values,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      });
      
      await batch.commit();
      console.log('✅ Taxonomy initialized successfully');
    }
  } catch (error) {
    console.error('Error initializing taxonomy:', error);
    throw error;
  }
};

/**
 * Get all taxonomy data
 */
export const getTaxonomyData = async () => {
  try {
    const taxonomyRef = collection(db, TAXONOMY_COLLECTION);
    const snapshot = await getDocs(taxonomyRef);
    
    const taxonomyData = {};
    snapshot.forEach(doc => {
      taxonomyData[doc.id] = {
        id: doc.id,
        ...doc.data()
      };
    });
    
    return taxonomyData;
  } catch (error) {
    console.error('Error getting taxonomy data:', error);
    throw error;
  }
};

/**
 * Get taxonomy values for a specific category
 */
export const getTaxonomyValues = async (category) => {
  try {
    const categoryRef = doc(db, TAXONOMY_COLLECTION, category);
    const categoryDoc = await getDoc(categoryRef);
    
    if (categoryDoc.exists()) {
      return categoryDoc.data().values || [];
    }
    
    return [];
  } catch (error) {
    console.error(`Error getting taxonomy values for ${category}:`, error);
    throw error;
  }
};

/**
 * Update taxonomy values for a category
 */
export const updateTaxonomyValues = async (category, values) => {
  try {
    const categoryRef = doc(db, TAXONOMY_COLLECTION, category);
    
    await updateDoc(categoryRef, {
      values: values,
      updatedAt: new Date().toISOString()
    });
    
    console.log(`✅ Taxonomy values updated for ${category}`);
    return true;
  } catch (error) {
    console.error(`Error updating taxonomy values for ${category}:`, error);
    throw error;
  }
};

/**
 * Add a new value to a taxonomy category
 */
export const addTaxonomyValue = async (category, valueObj) => {
  try {
    const categoryRef = doc(db, TAXONOMY_COLLECTION, category);
    
    await updateDoc(categoryRef, {
      values: arrayUnion(valueObj),
      updatedAt: new Date().toISOString()
    });
    
    console.log(`✅ Value "${valueObj.display}" added to ${category}`);
    return true;
  } catch (error) {
    console.error(`Error adding value to ${category}:`, error);
    throw error;
  }
};

/**
 * Remove a value from a taxonomy category
 */
export const removeTaxonomyValue = async (category, valueObj) => {
  try {
    const categoryRef = doc(db, TAXONOMY_COLLECTION, category);
    
    await updateDoc(categoryRef, {
      values: arrayRemove(valueObj),
      updatedAt: new Date().toISOString()
    });
    
    console.log(`✅ Value "${valueObj.display}" removed from ${category}`);
    return true;
  } catch (error) {
    console.error(`Error removing value from ${category}:`, error);
    throw error;
  }
};

/**
 * Validate taxonomy values for a product
 */
export const validateProductTaxonomy = (productData) => {
  const errors = {};
  
  // Check required fields
  Object.entries(TAXONOMY_CATEGORIES).forEach(([category, config]) => {
    if (config.required) {
      if (!productData[category] || 
          (Array.isArray(productData[category]) && productData[category].length === 0) ||
          (typeof productData[category] === 'string' && !productData[category].trim())) {
        errors[category] = `${config.name} is required`;
      }
    }
    
    // Check single value constraints
    if (config.singleValue && Array.isArray(productData[category])) {
      errors[category] = `${config.name} must be a single value`;
    }
    
    // Check array constraints
    if (!config.singleValue && !Array.isArray(productData[category])) {
      errors[category] = `${config.name} must be an array of values`;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Get taxonomy values for product creation form
 */
export const getTaxonomyForProductForm = async () => {
  try {
    const taxonomyData = await getTaxonomyData();
    
    // Transform data for form usage
    const formData = {};
    Object.entries(taxonomyData).forEach(([category, data]) => {
      formData[category] = {
        name: data.name,
        description: data.description,
        required: data.required,
        singleValue: data.singleValue,
        defaultValue: data.defaultValue,
        values: data.values || []
      };
    });
    
    return formData;
  } catch (error) {
    console.error('Error getting taxonomy for product form:', error);
    throw error;
  }
};

/**
 * Get taxonomy values for filtering
 */
export const getTaxonomyForFiltering = async () => {
  try {
    const taxonomyData = await getTaxonomyData();
    
    // Transform data for filtering
    const filterData = {};
    Object.entries(taxonomyData).forEach(([category, data]) => {
      filterData[category] = {
        name: data.name,
        values: data.values || []
      };
    });
    
    return filterData;
  } catch (error) {
    console.error('Error getting taxonomy for filtering:', error);
    throw error;
  }
};

/**
 * Check if a taxonomy value exists
 */
export const validateTaxonomyValue = async (category, value) => {
  try {
    const values = await getTaxonomyValues(category);
    return values.some(v => v.value === value);
  } catch (error) {
    console.error(`Error validating taxonomy value for ${category}:`, error);
    return false;
  }
};

/**
 * Get display value for a taxonomy value
 */
export const getDisplayValue = (category, value) => {
  const categoryValues = DEFAULT_TAXONOMY_VALUES[category] || [];
  const found = categoryValues.find(v => v.value === value);
  return found ? found.display : value;
};

/**
 * Get products by taxonomy filter
 */
export const getProductsByTaxonomyFilter = async (filters) => {
  try {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    let q = query(productsRef);
    
    // Apply taxonomy filters
    Object.entries(filters).forEach(([category, value]) => {
      if (value && value.length > 0) {
        if (Array.isArray(value)) {
          // Multi-value filter (e.g., dietaryTags)
          q = query(q, where(category, 'array-contains-any', value));
        } else {
          // Single value filter
          q = query(q, where(category, '==', value));
        }
      }
    });
    
    const snapshot = await getDocs(q);
    const products = [];
    
    snapshot.forEach(doc => {
      products.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return products;
  } catch (error) {
    console.error('Error getting products by taxonomy filter:', error);
    throw error;
  }
};